import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card } from '@/components/ui/card';
import { AlertTriangle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useOrganization } from '@/hooks/useOrganization';
import { getTier, SEAT_OVERAGE, type TierId } from '@/content/pricing';

/** Roles that consume an "admin" seat; everything else consumes a staff seat. */
const ADMIN_ROLES = new Set([
  'super_admin',
  'program_director',
  'manager',
  'supervisor',
]);

interface Props {
  tier: TierId;
}

/**
 * Warn-only plan usage panel. Shows client and seat usage against the
 * purchased tier's allotment. Nothing is blocked — over-limit organizations
 * simply see a notice so leadership can decide to upgrade.
 */
const PlanUsageCard: React.FC<Props> = ({ tier }) => {
  const { organization } = useOrganization();
  const orgId = organization?.id;
  const def = getTier(tier);

  const { data } = useQuery({
    queryKey: ['plan-usage', orgId],
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const [sitesRes, clientsRes, membersRes] = await Promise.all([
        supabase
          .from('sites')
          .select('id, name')
          .eq('organization_id', orgId!)
          .eq('is_active', true),
        supabase
          .from('clients')
          .select('id, site_id')
          .eq('organization_id', orgId!)
          .is('archived_at', null),
        supabase
          .from('organization_members')
          .select('user_id')
          .eq('organization_id', orgId!),
      ]);

      const sites = sitesRes.data ?? [];
      const clients = clientsRes.data ?? [];
      const userIds = (membersRes.data ?? []).map((m: any) => m.user_id);

      let adminSeats = 0;
      let staffSeats = 0;
      if (userIds.length) {
        const { data: roles } = await supabase
          .from('user_roles')
          .select('user_id, role')
          .in('user_id', userIds);
        const byUser = new Map<string, string>();
        (roles ?? []).forEach((r: any) => {
          const existing = byUser.get(r.user_id);
          if (!existing || ADMIN_ROLES.has(r.role)) byUser.set(r.user_id, r.role);
        });
        userIds.forEach((id) => {
          const role = byUser.get(id);
          if (role && ADMIN_ROLES.has(role)) adminSeats += 1;
          else staffSeats += 1;
        });
      }

      const perSite = sites.map((s: any) => ({
        name: s.name as string,
        count: clients.filter((c: any) => c.site_id === s.id).length,
      }));

      return { perSite, adminSeats, staffSeats, facilities: sites.length };
    },
  });

  if (!data || def.clients === null) return null;

  const facilityCount = Math.max(data.facilities, 1);
  const adminAllowance = (def.adminSeats ?? 0) * facilityCount;
  const staffAllowance = (def.staffSeats ?? 0) * facilityCount;

  const overClients = data.perSite.filter((s) => s.count > (def.clients ?? 0));
  const adminOver = Math.max(data.adminSeats - adminAllowance, 0);
  const staffOver = Math.max(data.staffSeats - staffAllowance, 0);
  const hasOverage = overClients.length > 0 || adminOver > 0 || staffOver > 0;

  const row = (label: string, used: number, allowed: number) => (
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span
        className={`tabular-nums ${used > allowed ? 'text-amber-700 font-medium' : 'text-foreground'}`}
      >
        {used.toLocaleString()} / {allowed.toLocaleString()}
      </span>
    </div>
  );

  return (
    <Card className="bg-card rounded-xl p-6 border space-y-3">
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="text-lg font-semibold text-foreground">Plan usage</h2>
        <span className="text-xs text-muted-foreground">
          {def.name} · {def.clients} clients per facility
        </span>
      </div>

      <div className="space-y-1.5">
        {data.perSite.map((s) => row(`${s.name} — active clients`, s.count, def.clients ?? 0))}
        {row('Admin seats', data.adminSeats, adminAllowance)}
        {row('Staff seats', data.staffSeats, staffAllowance)}
      </div>

      {hasOverage && (
        <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-50/60 p-3 text-xs text-amber-900">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <div className="space-y-1">
            <p className="font-medium">You are above your plan allowance.</p>
            <p>
              Nothing is blocked — your team keeps working as normal. Additional
              seats bill at ${SEAT_OVERAGE.admin}/admin and ${SEAT_OVERAGE.staff}/staff per
              month, or upgrade for a larger allowance.
            </p>
          </div>
        </div>
      )}
    </Card>
  );
};

export default PlanUsageCard;
